// DeliveryDetail.vue
<template>
  <div class="delivery-page">
    <Button class="back-button" @click="router.back()" severity="secondary" label="Retour" icon="pi pi-arrow-left" />

    <!-- Bannière mode hors ligne -->
    <div v-if="isUserOfflineMode" class="offline-banner mx-4 my-2">
      <i class="pi pi-info-circle mr-2"></i>
      <div>
        <div class="font-bold">Mode hors ligne activé</div>
        <div class="text-xs">Les données affichées sont celles disponibles en local. Les modifications seront synchronisées quand vous passerez en mode en ligne.</div>
      </div>
    </div>

    <!-- Bannière connexion perdue -->
    <div v-else-if="!isOnline" class="connection-lost-banner mx-4 my-2">
      <i class="pi pi-exclamation-triangle mr-2"></i>
      <div>
        <div class="font-bold">Connexion internet perdue</div>
        <div class="text-xs">Vous pouvez quand même effectuer des modifications, elles seront synchronisées automatiquement dès que la connexion sera rétablie.</div>
      </div>
    </div>

    <!-- Bannière erreurs de synchronisation -->
    <div v-if="hasSyncErrors" class="sync-error-banner mx-4 my-2">
      <i class="pi pi-exclamation-circle mr-2"></i>
      <div>
        <div class="font-bold">Erreur de synchronisation</div>
        <div class="text-xs">Des changements pour cette livraison n'ont pas pu être synchronisés après plusieurs tentatives.</div>
        <Button label="Réessayer maintenant" icon="pi pi-refresh" class="p-button-sm p-button-danger mt-2" @click="retrySync" :disabled="!isOnline || isSyncing" />
      </div>
    </div>

    <!-- Bannière changements en attente -->
    <div v-else-if="hasLocalChanges" class="pending-changes-banner mx-4 my-2">
      <i class="pi pi-clock mr-2"></i>
      <div>
        <div class="font-bold">Modifications en attente</div>
        <div class="text-xs">Des modifications ont été effectuées et sont en attente de synchronisation.</div>
        <Button label="Synchroniser maintenant" icon="pi pi-sync" class="p-button-sm p-button-info mt-2" @click="syncPendingUpdates" :disabled="!isOnline || isSyncing" />
      </div>
    </div>

    <ProgressSpinner v-if="loading" class="w-full flex justify-center my-4" />

    <div v-else-if="currentDelivery" class="delivery-container">

      <div class="cards-container">
        <DeliveryInfoCard :delivery="currentDelivery" />
        <DeliveryAssistanceCard :delivery="currentDelivery" />
      </div>

      <!-- Modale Anomalie -->
      <Dialog v-model:visible="showAnomalieModal" modal header="Signaler une anomalie" position="top"
        :style="{ width: '90vw', maxWidth: '500px' }">
        <AnomalieModal ref="anomalieModalRef" :delivery="currentDelivery" />
        <template #footer>
          <div class="dialog-footer">
            <div class="footer-content">
              <p v-if="isAnomalieFormValid" class="warning-text">
                Si l'anomalie n'est pas justifiée cela peut impacter votre prime
              </p>
              <div class="footer-buttons">
                <Button label="Retour" icon="pi pi-times" severity="secondary" text @click="closeAnomalieModal" />
                <Button label="Anomalie" icon="pi pi-check" severity="danger" @click="handleAnomalieConfirm"
                  :disabled="!isAnomalieFormValid" />
              </div>
            </div>
          </div>
        </template>
      </Dialog>
      

      <!-- Modale Conforme -->
      <Dialog v-model:visible="showConformeModal" modal header="Valider la livraison" position="top"
        :style="{ width: '90vw', maxWidth: '500px' }">
        <ConformeModal ref="conformeModalRef" :delivery="currentDelivery" />
        <template #footer>
          <div class="dialog-footer">
            <div class="footer-content">
              <div class="footer-buttons">
                <Button label="Annuler" icon="pi pi-times" severity="secondary" text @click="closeConformeModal" />
                <Button label="Confirmer" icon="pi pi-check" severity="success" @click="handleConformeConfirm"
                  :disabled="!isConformeFormValid" />
              </div>
            </div>
          </div>
        </template>
      </Dialog>

      <!-- Boutons d'action fixes -->
      <div class="action-buttons">
        <Button label="Valider la livraison" icon="pi pi-check" severity="success" class="validate-button"
          @click="openConformeModal" />
        <Button label="Signaler un problème" icon="pi pi-times" severity="danger" class="problem-button"
          @click="openAnomalieModal" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import localforage from 'localforage';
import { syncService } from '@/services/syncService';
import axios from '@/api/axios'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import DeliveryInfoCard from './DeliveryDetail/DeliveryInfoCard.vue'
import DeliveryAssistanceCard from './DeliveryDetail/DeliveryAssistanceCard.vue'
import AnomalieModal from './Anomalies/AnomalieModal.vue'
import ConformeModal from './Conformes/ConformeModal.vue'

// Props
const props = defineProps({
  fallbackDelivery: {
    type: Object,
    default: null
  }
})

// Composables
const store = useStore()
const router = useRouter()
const route = useRoute()
const toast = useToast()

// Reactive state
const loading = ref(false)
const hasLocalChanges = ref(false)
const hasSyncErrors = ref(false)
const isSyncing = computed(() => store.getters['sync/isSyncing'])

// Refs pour les modales
const anomalieModalRef = ref(null)
const conformeModalRef = ref(null)
const showAnomalieModal = ref(false)
const showConformeModal = ref(false)

// Computed
const currentDelivery = computed(() => store.state.currentDelivery)
const isOnline = computed(() => store.getters['sync/isOnline'])
const isUserOfflineMode = computed(() => store.getters.isUserOfflineMode)
const hasPendingUpdates = computed(() => store.getters['sync/hasPendingUpdates'])

// État du formulaire d'anomalie
const isAnomalieFormValid = computed(() => {
  return anomalieModalRef.value?.isFormValid ?? false
})

const isConformeFormValid = computed(() => {
  return conformeModalRef.value?.isFormValid ?? false
})

// Vérifier si cette livraison a des mises à jour en attente et s'il y a des erreurs
const checkForLocalChanges = async () => {
  if (!currentDelivery.value?.id) return;
  
  try {
    const pendingData = await syncService.exportPendingData();
    
    // Filtrer les mises à jour pour cette livraison spécifique
    const deliveryUpdates = pendingData.updates.filter(
      update => update.deliveryId === currentDelivery.value.id
    );
    
    // Vérifier s'il y a des mises à jour en attente pour cette livraison
    hasLocalChanges.value = deliveryUpdates.length > 0;
    
    // Vérifier si une des mises à jour a échoué plusieurs fois
    hasSyncErrors.value = deliveryUpdates.some(
      update => update.requiresManualSync || (update.syncAttempts && update.syncAttempts >= 3)
    );
    
    // Log pour diagnostic
    if (hasSyncErrors.value) {
      console.warn(`🚨 Des erreurs de synchronisation détectées pour la livraison ${currentDelivery.value.id}`);
    }
    
  } catch (error) {
    console.error('Erreur lors de la vérification des modifications locales:', error);
    hasLocalChanges.value = false;
    hasSyncErrors.value = false;
  }
};

// Fonction pour réessayer la synchronisation de cette livraison spécifique
const retrySync = async () => {
  if (!isOnline.value || isSyncing.value) return;
  
  try {
    // Afficher un message de chargement
    toast.add({
      severity: 'info',
      summary: 'Synchronisation',
      detail: 'Tentative de synchronisation en cours...',
      life: 3000
    });
    
    // Déclencher la synchronisation complète
    const result = await store.dispatch('sync/synchronize');
    
    // Vérifier à nouveau s'il y a des changements locaux après la tentative
    await checkForLocalChanges();
    
    if (!hasSyncErrors.value) {
      toast.add({
        severity: 'success',
        summary: 'Synchronisation réussie',
        detail: 'Les modifications ont été synchronisées avec succès',
        life: 3000
      });
    } else {
      // Si les erreurs persistent
      toast.add({
        severity: 'error',
        summary: 'Échec de synchronisation',
        detail: 'Les modifications n\'ont pas pu être synchronisées. Veuillez réessayer ultérieurement ou contacter le support.',
        life: 5000
      });
    }
  } catch (error) {
    console.error('Erreur lors de la resynchronisation:', error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors de la synchronisation',
      life: 3000
    });
  }
};

const fetchDelivery = async () => {
  const deliveryId = route.params.id;
  if (!deliveryId) return;

  loading.value = true;

  try {
    // Vérifier d'abord si nous avons une mise à jour en attente
    await checkForLocalChanges();

    // Si la livraison est déjà dans le store et a des propriétés, on la garde
    if (store.state.currentDelivery?.id === deliveryId && Object.keys(store.state.currentDelivery).length > 3) {
      console.log("Utilisation des données de livraison déjà en mémoire");
      loading.value = false;
      return;
    }

    // Utiliser l'action du store sync pour une stratégie plus robuste
    try {
      const deliveryData = await store.dispatch('sync/getDelivery', deliveryId);
      if (deliveryData) {
        store.commit('SET_CURRENT_DELIVERY', deliveryData);
        console.log("Données récupérées via le store sync");
      } else {
        throw new Error("Aucune donnée de livraison trouvée");
      }
    } catch (storeError) {
      console.warn("Échec de récupération via le store sync:", storeError);
      
      // Stratégie adaptative pour récupérer les données, en cas d'échec du store
      let deliveryData = null;

      // 1. Essayer l'API si en ligne et pas en mode hors ligne explicite
      if (isOnline.value && !isUserOfflineMode.value) {
        try {
          console.log("Tentative de récupération directe depuis l'API...");
          const response = await axios.get(`/livraison/delivery/${deliveryId}/`);
          deliveryData = response.data;
          console.log("Données récupérées depuis l'API avec succès");
          
          // Mettre à jour le cache avec les nouvelles données
          await syncService.cacheDelivery(deliveryId, deliveryData);
        } catch (apiError) {
          console.warn("Échec de récupération depuis l'API:", apiError);
          // On continue avec les autres stratégies
        }
      }

      // 2. Si pas de données de l'API, essayer le cache
      if (!deliveryData) {
        try {
          console.log("Tentative de récupération depuis le cache...");
          deliveryData = await syncService.getCachedDelivery(deliveryId);
          if (deliveryData) {
            console.log("Données récupérées depuis le cache local");
          }
        } catch (cacheError) {
          console.warn("Échec de récupération depuis le cache:", cacheError);
        }
      }

      // 3. Si toujours pas de données, essayer de les extraire de la tournée
      if (!deliveryData && store.state.tourneeData?.deliveries) {
        const deliveryFromTournee = store.state.tourneeData.deliveries.find(d => d.id === deliveryId);
        if (deliveryFromTournee) {
          deliveryData = deliveryFromTournee;
          console.log("Données extraites de la tournée en mémoire");
        }
      }

      // Mettre à jour le store avec les données récupérées
      if (deliveryData) {
        store.commit('SET_CURRENT_DELIVERY', deliveryData);
      } else {
        // Aucune donnée disponible
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de récupérer les données de livraison. Vérifiez votre connexion ou réessayez plus tard.',
          life: 5000
        });
      }
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la livraison:', error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors du chargement des données',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
};

const handleAnomalieConfirm = async () => {
  try {
    const result = await anomalieModalRef.value?.handleConfirm();

    if (result?.success) {
      closeAnomalieModal();

      // Marquer comme ayant des changements locaux
      hasLocalChanges.value = true;

      toast.add({
        severity: isOnline.value && !isUserOfflineMode.value ? 'success' : 'info',
        summary: isOnline.value && !isUserOfflineMode.value ? 'Problème signalé' : 'Mode hors ligne',
        detail: isOnline.value && !isUserOfflineMode.value
          ? 'Le problème a été signalé avec succès'
          : 'Le problème sera synchronisé lors de la prochaine connexion',
        life: 3000
      });

      // Mettre à jour explicitement le store avant la redirection
      const updatedTournee = { ...store.state.tourneeData };
      if (updatedTournee && updatedTournee.deliveries) {
        // Mettre à jour la livraison dans la tournée en mémoire
        const deliveryIndex = updatedTournee.deliveries.findIndex(d => d.id === currentDelivery.value.id);
        if (deliveryIndex !== -1) {
          updatedTournee.deliveries[deliveryIndex].status = 'NOT DELIVERED';
          updatedTournee.deliveries[deliveryIndex].undone_reason = result.undoneReason;
          // Mise à jour directe du store pour garantir que la vue de liste a les données
          store.commit('SET_TOURNEE_DATA', updatedTournee);
        }
      }

      // Redirection immédiate
      router.back();
    }
  } catch (error) {
    console.error('Erreur lors du signalement:', error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors du signalement',
      life: 3000
    })
  }
}

const handleConformeConfirm = async () => {
  if (!conformeModalRef.value) {
    console.error('Référence à la modale non disponible');
    return;
  }

  try {
    console.log("Début de la confirmation de livraison...");
    // Appeler la méthode handleConfirm de la modale
    const result = await conformeModalRef.value.handleConfirm();
    console.log("Résultat de handleConfirm:", result);

    if (result) {
      console.log("Fermeture de la modale...");
      // Fermer la modale immédiatement
      showConformeModal.value = false;

      // Marquer comme ayant des changements locaux
      hasLocalChanges.value = true;

      // Mettre à jour explicitement la tournée dans le store
      if (store.state.tourneeData?.deliveries) {
        const updatedTournee = { ...store.state.tourneeData };
        const deliveryIndex = updatedTournee.deliveries.findIndex(d => d.id === currentDelivery.value.id);

        if (deliveryIndex !== -1) {
          console.log("Mise à jour de la livraison dans la tournée...");
          // Créer un objet propre pour éviter les références à des objets non-clonables
          updatedTournee.deliveries[deliveryIndex] = {
            ...updatedTournee.deliveries[deliveryIndex],
            status: 'DELIVERED',
            consignes_rendues_v2: result.consignes_rendues_v2
          };
          store.commit('SET_TOURNEE_DATA', updatedTournee);
        }
      }

      // Mise à jour du currentDelivery dans le store
      store.commit('UPDATE_CURRENT_DELIVERY', {
        status: 'DELIVERED',
        consignes_rendues_v2: result.consignes_rendues_v2
      });
      
      // Émettre un événement explicite pour mettre à jour l'interface partout où nécessaire
      if (window.app) {
        try {
          const eventBus = window.app._context.provides.eventBus;
          if (eventBus && typeof eventBus.deliveryUpdated === 'function') {
            eventBus.deliveryUpdated(currentDelivery.value.id, {
              status: 'DELIVERED',
              consignes_rendues_v2: result.consignes_rendues_v2
            });
            console.log("Événement delivery-updated émis depuis handleConformeConfirm");
          }
        } catch (eventError) {
          console.error("Erreur lors de l'émission de l'événement:", eventError);
        }
      }

      // Mettre à jour le cache localforage
      try {
        // Création d'un nouvel objet propre pour le cache
        const deliveryToCache = {
          ...currentDelivery.value,
          status: 'DELIVERED',
          consignes_rendues_v2: result.consignes_rendues_v2
        };
        
        // Supprimer les propriétés Vue.js qui pourraient causer des problèmes
        delete deliveryToCache.__ob__;
        delete deliveryToCache._isVue;
        
        // Mise à jour du cache
        await syncService.cacheDelivery(currentDelivery.value.id, deliveryToCache);
        console.log("Cache mis à jour avec succès");

      } catch (cacheError) {
        console.error("Erreur lors de la mise à jour du cache:", cacheError);
      }

      // Afficher le toast approprié selon l'état de la connexion
      toast.add({
        severity: isOnline.value && !isUserOfflineMode.value ? 'success' : 'info',
        summary: 'Livraison validée',
        detail: isOnline.value && !isUserOfflineMode.value
          ? 'La livraison a été marquée comme effectuée'
          : 'La livraison sera synchronisée lors de la prochaine connexion',
        life: 3000
      });

      console.log("Navigation vers la page précédente...");
      // Navigation après un court délai pour permettre à Vue de mettre à jour le DOM
      setTimeout(() => {
        router.back();
      }, 100);
    }
  } catch (error) {
    console.error('Erreur lors de la validation:', error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors de la validation',
      life: 3000
    });
  }
}

const preloadDeliveryComponents = async () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      // Précharger les données de la livraison
      const deliveryId = route.params.id;
      if (deliveryId) {
        // Création d'un objet propre et sérialisable pour le Service Worker
        // en évitant les propriétés non-clonables
        const sanitizedData = currentDelivery.value ? sanitizeDeliveryData(currentDelivery.value) : null;
        
        // Envoi uniquement des données essentielles et sérialisables
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_DELIVERY',
          deliveryId,
          // Envoyer un objet minimal avec seulement les propriétés essentielles
          data: sanitizedData
        });
      }
    } catch (error) {
      console.error('Erreur lors du préchargement:', error);
    }
  }
};

// Fonction pour sanitiser les données de livraison avant envoi au Service Worker
const sanitizeDeliveryData = (delivery) => {
  if (!delivery) return null;
  
  try {
    // Créer un nouvel objet avec seulement les propriétés essentielles
    const sanitized = {
      id: delivery.id,
      delivery_date: delivery.delivery_date,
      status: delivery.status,
      organisation: delivery.organisation ? {
        id: delivery.organisation.id,
        name: delivery.organisation.name,
        phone: delivery.organisation.phone
      } : null,
      deliveryLocation: delivery.deliveryLocation ? {
        id: delivery.deliveryLocation.id,
        name: delivery.deliveryLocation.name,
        address: delivery.deliveryLocation.address,
        delivery_instructions: delivery.deliveryLocation.delivery_instructions
      } : null,
      route_position: delivery.route_position,
      undone_reason: delivery.undone_reason,
      // Ajouter d'autres propriétés essentielles si nécessaire, mais garder au minimum
    };
    
    // Assurer que l'objet est complètement sérialisable en le convertissant en JSON puis en objet
    return JSON.parse(JSON.stringify(sanitized));
  } catch (error) {
    console.error('Erreur lors de la sanitisation des données:', error);
    // En cas d'erreur, retourner un objet minimal avec uniquement l'ID
    return { id: delivery.id };
  }
};

const openAnomalieModal = () => {
  if (!currentDelivery.value) {
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Données de livraison non disponibles',
      life: 3000
    })
    return
  }
  showAnomalieModal.value = true
}

const closeAnomalieModal = () => {
  showAnomalieModal.value = false
}

const openConformeModal = () => {
  showConformeModal.value = true
}

const closeConformeModal = () => {
  showConformeModal.value = false
}

// Synchronisation manuelle des mises à jour en attente
const syncPendingUpdates = async () => {
  try {
    console.log("Tentative de synchronisation...");
    isSyncing.value = true;
    
    // Utiliser le module de synchronisation pour synchroniser toutes les mises à jour
    const result = await store.dispatch('sync/synchronize');
    
    if (result && result.success) {
      // Mettre à jour l'état local
      hasLocalChanges.value = false;
      
      // Rafraîchir les données de la livraison après la synchronisation
      await fetchDelivery();
      
      toast.add({
        severity: 'success',
        summary: 'Synchronisation',
        detail: 'Données synchronisées avec succès',
        life: 3000
      });
    } else {
      throw new Error(result?.error || 'Échec de la synchronisation');
    }
  } catch (error) {
    console.error("Erreur de synchronisation:", error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Échec de la synchronisation: ' + (error.message || 'Erreur inconnue'),
      life: 3000
    });
  } finally {
    isSyncing.value = false;
    
    // Vérifier à nouveau s'il reste des changements locaux
    await checkForLocalChanges();
  }
};

onMounted(async () => {
  try {
    await fetchDelivery();
    
    // Register for online/offline events to automatically refresh when connection changes
    window.addEventListener('online', async () => {
      if (currentDelivery.value?.id) {
        console.log('Connexion rétablie, actualisation des données...');
        await fetchDelivery();
      }
    });
    
    await preloadDeliveryComponents();
  } catch (error) {
    console.error('Erreur lors du chargement initial:', error);
  }
});
</script>

<style scoped>
.delivery-page {
  padding-bottom: 4rem;
  width: 100%;
  max-width: 100%;
}

.delivery-container {
  width: 100%;
  max-width: 100%;
}

.cards-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  max-width: 100%;
}

.back-button {
  width: 100%;
  justify-content: flex-start !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0 !important;
  font-size: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.back-button .pi {
  margin-right: 0.5rem;
}

.action-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 1rem;
  padding: 0.5rem;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.validate-button,
.problem-button {
  flex: 1;
  padding: 0.75rem 1rem;
  height: 3.5rem;
  /* Hauteur augmentée pour tous les écrans */
}

/* Ajustements spécifiques pour les écrans mobiles */
@media (max-width: 768px) {

  .validate-button,
  .problem-button {
    height: 4rem;
    /* Hauteur encore plus grande sur mobile */
    font-size: 1.1rem;
    /* Texte légèrement plus grand */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-buttons {
    padding: 0.75rem;
    /* Plus de rembourrage autour des boutons */
  }

  /* Augmenter la taille des icônes */
  .validate-button .pi,
  .problem-button .pi {
    font-size: 1.25rem;
    margin-right: 0.5rem;
  }
}


.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
}

@media (min-width: 768px) {
  .back-button {
    max-width: 200px;
    border-radius: var(--border-radius) !important;
    margin: 1rem;
  }
}

.warning-text {
  /* Couleur du texte en rouge */
  color: var(--red-600);

  /* Taille de police légèrement réduite */
  font-size: 0.875rem;

  /* On supprime les marges par défaut */
  margin: 0;

  /* Centrage du texte */
  text-align: center;

  /* Style de texte en italique pour l'emphase */
  font-style: italic;

  /* Bordure rouge solide de 2px */
  border: 2px solid var(--red-600);

  /* Espacement intérieur pour séparer le texte de la bordure */
  padding: 0.5rem;

  /* Coins arrondis */
  border-radius: 4px;

  /* Couleur de fond légèrement rouge pour le contraste */
  background-color: var(--red-50);

  padding-bottom: 1rem;
}

/* Bannières d'information */
.offline-banner {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 0.5rem;
  color: #856404;
}

.connection-lost-banner {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 0.5rem;
  color: #721c24;
}

.pending-changes-banner {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #cce5ff;
  border: 1px solid #b8daff;
  border-radius: 0.5rem;
  color: #004085;
}

/* Style pour la bannière d'erreur de synchronisation */
.sync-error-banner {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 0.5rem;
  color: #721c24;
}

/* Styles modifiés pour les bannières d'information */
.offline-banner,
.connection-lost-banner,
.pending-changes-banner,
.sync-error-banner {
  display: flex;
  align-items: flex-start; /* Alignement en haut pour un meilleur rendu avec du contenu multiple */
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
}

.offline-banner {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
}

.connection-lost-banner {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.pending-changes-banner {
  background-color: #cce5ff;
  border: 1px solid #b8daff;
  color: #004085;
}
</style>